<template>
  <v-app>
    <PlaylistCarousel v-if="!loading && displayPlaylistScreen" class="full-screen" /> 
    <v-container>
      <app-head 
        aria-label="App Head"
        class="app-head" 
        role="presentation"
        :stopDescription="stopDescription"
        :multiStop="multiStop" 
        :digitalCodeDescription="digitalCodeDescription"
        />
      <vue-cookie-accept-decline
        elementId="cookiepreference"
        position="bottom-right"
        ref="cookiepreference"
        transitionName="slideFromBottom"
        type="floating"
        :debug="false"
        :disableDecline="false"
        :showPostponeButton="true"
        @clicked-accept="cookieClickedAccept"
        @clicked-decline="cookieClickedDecline"
      >
        <template #message>
          <div class="text-primary">
            We use cookies to ensure you get the best experience on our website.
          </div>
          <a href="/cookies.html" target="_blank">Learn More...</a>
        </template>
        <template #declineContent>Decline</template>
        <template #acceptContent>Agree</template>
      </vue-cookie-accept-decline>
      <time-table v-if="currentPage === 'timetable'" class="time-table" />
      <nav-drawer
        v-if="!disableMenu"
        @showCookieSettings="showCookieSettings"
      />
      <message-page
        v-if="currentPage === 'messages'"
        class="message-page"
        :latestMessageOnly="false"
      />
      <links-page v-if="currentPage === 'links'" class="links-page" />
    </v-container>
    <app-footer 
      v-if="!displayPlaylistScreen" 
      class="app-footer" 
      :messageType="messageType" 
    />
  </v-app>
</template>

<script>
import NavDrawer from "./components/NavDrawer.vue";
import AppHead from "./components/AppHead.vue";
import PlaylistCarousel from "./components/PlaylistCarousel.vue";
import TimeTable from "./components/TimeTable.vue";
import MessagePage from "./components/MessagePage.vue";
import LinksPage from "./components/LinksPage.vue";
import AppFooter from "./components/AppFooter.vue";
import VueCookieAcceptDecline from "vue-cookie-accept-decline";
import { MessageLocation } from "../constants";

import { mapState, mapMutations, mapGetters, mapActions } from "vuex";

export default {
  name: "App",
  components: {
    NavDrawer,
    AppHead,
    PlaylistCarousel,
    TimeTable,
    MessagePage,
    LinksPage,
    AppFooter,
    VueCookieAcceptDecline,
  },
  data() {
    return {
      loading: true,
      src: require("@/assets/splash_screen_logo.svg"),
      alt: 'transport for west midlands buses',
    };
  },
  computed: {
    ...mapGetters([
      "cookiePreference",
      "getmessagesForQRCode",
      "displayPlaylistScreen",
      "getHighestPriorityMessage"
    ]),
    ...mapState([
      "qRCode",
      "currentPage",
      "disableMenu",
      "stopDescription",
      "multiStop",
      "digitalCodeDescription",
      "pageTitle",
      "playlist"
    ]),

    messageType() {
      return this.getHighestPriorityMessage?.type ?? MessageLocation.FOOTER;
    },
  },
  methods: {
    ...mapMutations(["recreateAllCookies", "deleteAllCookies", "setQRCode"]),
    ...mapActions(["updateRepressServices", "updateCurrentPage", "fetchData", "setIsHealthCheckRequest", "detectNetworkQuality"]),
    showCookieSettings() {
      this.$refs.cookiepreference.removeCookie();
      this.$refs.cookiepreference.init();
    },
    cookieClickedAccept() {
      this.recreateAllCookies();
    },
    cookieClickedDecline() {
      this.deleteAllCookies();
    },
    getIsHealthCheckRequest() {
      const healthCheckString = new URLSearchParams(window.location.search).get('healthcheck');
      const isHealthCheckRequest = healthCheckString === "true";

      return isHealthCheckRequest;    
    }
  },
  mounted() {
    this.detectNetworkQuality();
    this.updateRepressServices(false);
    document.title = this.pageTitle 

    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 0);
  },
  watch: {
    $route() {
      this.setIsHealthCheckRequest(this.getIsHealthCheckRequest());     
      this.setQRCode(this.$route.params.id ?? '');

      const minSplashTime = 500; // milliseconds

      const splashTimer = new Promise(resolve => setTimeout(resolve, minSplashTime));
  
      const apiCall = this.fetchData();

      Promise.all([splashTimer, apiCall]).then(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style >

.loading-ui {
  position: fixed !important;
  top: calc(max(var(--toolbar-height) , var(--min-toolbar-height)) + var(--app-head-top));
  max-height: 100vh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
  z-index: 99999;
}
.cookie__floating__postpone-button {
  border: none;            
  padding: 0px 7px;      
  border-radius: 5px;     
  z-index: -1;
  float: right;
  font-size: 30px;
}
:root {
  --toolbar-height: 64px;
  --min-toolbar-height: 64px;
  --app-head-top: 64px
}

.app-footer {
  position: fixed !important;
  bottom: 0;
  left: 0;
  display: flex;
  width: 100%;
  flex-direction: column;
}

.information-popup {
  margin: 0px;
  width: 100%;
  overflow-y: auto;

  flex: 1;
  min-height: 5vh;
  position: relative;
}

.time-table {
  position: fixed !important;
  top: calc(max(var(--toolbar-height) , var(--min-toolbar-height)) + var(--app-head-top));
  max-height: 80vh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
}

.nav-drawer {
  position: fixed !important;
  top: 0vh;
  left: 0;
  width: 100%;
  z-index: 1011 !important;
  height: 100vh !important;
}
.app-head {
  position: fixed !important;
  top: var(--app-head-top) !important;
  height: var(--toolbar-height);
  min-height: var(--min-toolbar-height);
  left: 0;
  width: 100%;
  z-index: 1000;
}

.full-screen {
  position: fixed !important;
  top: 0vh;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 1015;
}

.message-page {
  position: fixed !important;
  top: calc(max(var(--toolbar-height) , var(--min-toolbar-height)) + var(--app-head-top));
  max-height: 80dvh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
}
.links-page {
  position: fixed !important;
  top: calc(max(var(--toolbar-height) , var(--min-toolbar-height)) + var(--app-head-top));
  max-height: 80dvh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;

  height: 101vh; /* Allow scroll to dismiss ios browser address bar*/
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.menu-app-bar{
  height: var(--app-head-top) !important;
}
.splash-screen-container {
  height:100%;
  background: rgb(var(--v-theme-splash-footer));
}

.splash-main {  
  display: flex;
  justify-content: center;  /* horizontal center */
  align-items: center;      /* vertical center */
  left: 0;
  width: 100%;
  margin: 0px !important;
  background: rgb(var(--v-theme-splash-footer)) !important;
  color: white;
  padding: 0px;
  margin: 0px !important;
}

.splash {
  max-width: 100% !important;
  background-color: rgb(var(--v-theme-splash-footer)) !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}


@media (orientation: landscape) {
  .message-page{
    max-height: 80dvh;
  }
  .links-page {
    max-height: 80dvh;
  }
}
</style>

